import { useQuery } from "@tanstack/react-query";
import { Loader2, Truck } from "lucide-react";
import { DeliveryTimeline } from "./DeliveryTimeline";
import { DeliveryRouteMap } from "./DeliveryRouteMap";
import { Card, CardContent } from "./ui/card";

type DeliveryRequestData = {
  id: string;
  status: string;
  acceptedByUserId?: string | null;
  driverName?: string | null;
  stops?: { sellerName: string; status?: string; pickedUpAt?: string | null }[];
  completedAt?: string | null;
  createdAt?: string | null;
  deliverByDate?: string | null;
  buyerAddressLine1?: string;
  buyerCity?: string;
  buyerState?: string;
  buyerLat?: string | number | null;
  buyerLng?: string | number | null;
  deliveryFee?: string | number | null;
};

type DriverLocationData = {
  tracking: boolean;
  latitude?: number;
  longitude?: number;
  heading?: number | null;
  speed?: number | null;
  updatedAt?: string;
  ageSeconds?: number;
};

type Props = {
  orderId: string;
};

export function LiveDeliveryTracker({ orderId }: Props) {
  const { data: drData, isLoading: drLoading } = useQuery<{ deliveryRequest: DeliveryRequestData | null }>({
    queryKey: ["/api/orders", orderId, "delivery-request"],
    queryFn: async () => {
      const res = await fetch(`/api/orders/${orderId}/delivery-request`, { credentials: "include" });
      if (!res.ok) return { deliveryRequest: null };
      return res.json();
    },
    refetchInterval: 20_000,
    refetchIntervalInBackground: true,
  });

  const deliveryRequest = drData?.deliveryRequest;
  const isActive = deliveryRequest?.status === "accepted" || deliveryRequest?.status === "in_progress";

  const { data: locationData } = useQuery<DriverLocationData>({
    queryKey: ["/api/delivery-tracking", deliveryRequest?.id, "location"],
    queryFn: async () => {
      const res = await fetch(`/api/delivery-tracking/${deliveryRequest!.id}/location`, { credentials: "include" });
      if (!res.ok) return { tracking: false };
      return res.json();
    },
    enabled: Boolean(deliveryRequest?.id) && isActive,
    // refetchIntervalInBackground: the Capacitor WebView often reports itself as
    // unfocused even while the user is watching, which silently suspends the
    // default interval — that's why the marker only moved on remount. Force it.
    refetchInterval: 6_000,
    refetchIntervalInBackground: true,
  });

  if (drLoading) {
    return (
      <Card className="text-left mb-4">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading delivery status...
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!deliveryRequest) return null;

  const isTracking = locationData?.tracking && locationData.latitude && locationData.longitude;
  const feeNum = deliveryRequest.deliveryFee != null ? Number(deliveryRequest.deliveryFee) : null;

  return (
    <Card className="text-left mb-4">
      <CardContent className="pt-4 pb-4 space-y-4">
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" />
          <h3 className="font-semibold">Delivery tracking</h3>
          {isTracking && (
            <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
              Live
            </span>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          Stoop arranges the driver for this delivery — sellers don't need to find one, just have the order ready for pickup.
        </p>

        <DeliveryTimeline
          deliveryRequest={deliveryRequest}
          driverName={deliveryRequest.driverName}
        />

        <DeliveryRouteMap
          stops={(deliveryRequest.stops ?? []).map((s) => ({
            label: s.sellerName,
            lat: (s as any).lat,
            lng: (s as any).lng,
            status: s.status,
          }))}
          destination={{
            label: `${deliveryRequest.buyerCity ?? ""}${deliveryRequest.buyerState ? `, ${deliveryRequest.buyerState}` : ""}`,
            lat: deliveryRequest.buyerLat,
            lng: deliveryRequest.buyerLng,
          }}
          driverLocation={isTracking ? { lat: locationData.latitude!, lng: locationData.longitude! } : null}
          height={260}
        />

        {isTracking && (
          <p className="text-center text-[11px] text-muted-foreground">
            Driver location updated {locationData.ageSeconds != null && locationData.ageSeconds < 60
              ? `${locationData.ageSeconds}s ago`
              : locationData.ageSeconds != null
                ? `${Math.round(locationData.ageSeconds / 60)}m ago`
                : "recently"}
            {locationData.speed != null && locationData.speed > 0 && ` · ${Math.round(locationData.speed)} mph`}
          </p>
        )}

        {isActive && !isTracking && (
          <p className="text-center text-[11px] text-muted-foreground">
            {deliveryRequest.status === "accepted"
              ? "Route preview shown above. The live driver marker appears once they head out to start pickups."
              : "Driver location paused — the live marker will return when they resume."}
          </p>
        )}

        {(deliveryRequest.buyerAddressLine1 || deliveryRequest.buyerCity || feeNum) && (
          <div className="text-sm text-muted-foreground space-y-1 border-t pt-3">
            {(deliveryRequest.buyerAddressLine1 || deliveryRequest.buyerCity) && (
              <p>
                Delivering to: {deliveryRequest.buyerAddressLine1 ?? ""}
                {deliveryRequest.buyerCity ? `${deliveryRequest.buyerAddressLine1 ? ", " : ""}${deliveryRequest.buyerCity}` : ""}
              </p>
            )}
            {feeNum != null && Number.isFinite(feeNum) && (
              <p>Delivery fee: ${feeNum.toFixed(2)}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
